import { Close, Description, PlayArrow } from "@mui/icons-material";
import { Button, CircularProgress, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Dispatch, SetStateAction, useState, useEffect } from "react";
import { useSessionContext } from "../../contexts/SessionContext";
import { useFhirData } from "../../utils/useFhirData";
import { useClinicalRegisterContext } from "../../contexts/ClinicalRegisterContext";
import { GetRelatedPerson } from "../../services/fhir/relatedperson/GetRelatedPerson";
import { oids } from "../../configs/Settings";
import { VerifyResponsibleDialog } from "./VerifyResponsibleDialog";
import { WarningAppointment } from "@/pages/schedule/components/WarningAppointment";
import { GetPaymentNotice } from "@/services/fhir/paymentnotice/GetPaymentNotice";
import { useContractContext } from "@/contexts/ContractContext";

interface IProfessionalActions {
  status: string;
  dataAppointment: fhir5.Appointment;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setPayment: Dispatch<SetStateAction<any>>;
  setIsPaid: Dispatch<SetStateAction<boolean>>;
  setRefetch?: Dispatch<SetStateAction<boolean>>;
  fetchAppointments?: () => Promise<void>;
}

const primaryButton: any = {
  icon: <Description />,
  text: {
    booked: "Registros clínicos",
    arrived: "Registros clínicos",
    proposed: "Registros clínicos",
    pending: "Registros clínicos",
  },
  backgroundColor: {
    booked: "primary700.main",
    arrived: "primary700.main",
    proposed: "neutral700.main",
    pending: "neutral700.main",
  },

  hover: {
    booked: "primary800.main",
    arrived: "primary800.main",
    proposed: "neutral600.main",
    pending: "neutral600.main",
  },
};

const secondaryButtonStyle: any = {
  icon: {
    booked: <PlayArrow />,
    arrived: <PlayArrow />,
    proposed: <Close />,
    pending: <PlayArrow />,
  },
  text: {
    booked: "Iniciar atendimento",
    arrived: "Iniciar atendimento",
    proposed: "Cancelar consulta",
    pending: "Iniciar atendimento",
  },
  borderColor: {
    booked: "primary700.main",
    arrived: "primary700.main",
    proposed: "error600.main",
    pending: "error600.main",
  },
  color: {
    booked: "primary700.main",
    arrived: "primary700.main",
    proposed: "error600.main",
    pending: "error600.main",
  },
};

export function ProfessionalActions({
  status,
  setIsOpen,
  dataAppointment,
  setPayment,
  setIsPaid,
  setRefetch,
  fetchAppointments,
}: IProfessionalActions) {
  const navigate = useNavigate();
  const { access } = useSessionContext();
  const { currentContract } = useContractContext();

  const { verifyEncounter, inProgress } = useClinicalRegisterContext();

  const [loading, setLoading] = useState(false);
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);
  const [warningAction, setWarningAction] = useState<
    "navigate" | "startService"
  >("navigate");

  const patientId = useFhirData
    .findParticipant(dataAppointment?.participant, "Patient")
    ?.reference?.split("-")?.[1];

  const hasSubscriptionFluxPay =
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: any) =>
        coding.system.includes("contract-type") &&
        coding.value === "fluxPayPlan"
    );

  const styleSXSecondary = {
    fontSize: 16,
    border: "1px solid",
    borderColor: secondaryButtonStyle.borderColor[status || "pending"],
    gap: 1,
    borderRadius: 3,
    alignItems: "center",
    justifyContent: "center",
    textTransform: "none",
    color: secondaryButtonStyle.color[status || "pending"],
    backgroundColor: "transparent",
    ":hover": {
      color: "primary800.main",
    },
  };

  const [verifyDialog, setVerifyDialog] = useState<boolean>(false);
  const [related, setRelated] = useState<any>([]);

  async function fetchRelated() {
    const relatedResponse = await GetRelatedPerson({
      patient: `${oids.cpf}-${patientId}`,
    }).then((response) => response?.data?.entry);
    const verifyRelated = relatedResponse?.filter(
      (el: any) =>
        el?.resource?.identifier &&
        !el?.resource?.active &&
        el?.resource?.period?.start
    );
    if (verifyRelated?.length) setRelated(verifyRelated);
    else setRelated([]);
    return verifyRelated;
  }

  // Função auxiliar para verificar se o atendimento foi pago
  const checkAppointmentPayment = () => {
    // Verificação baseada no status do atendimento
    // Se for 'pending' não foi pago, se for 'booked' foi pago
    return dataAppointment?.status === "booked";
  };

  // Função para navegar para a página de sumário
  const navigateToSummary = () => {
    if (patientId) {
      navigate("/summary", {
        state: {
          patientId,
          appointment: dataAppointment,
        },
      });
    }
  };

  // Função para buscar o PaymentNotice
  const fetchPayment = async () => {
    if (dataAppointment?.id) {
      // Encontra o participant que é o paciente
      const patientParticipant = dataAppointment.participant?.find((p) =>
        p.actor?.reference?.startsWith("Patient/")
      );

      // Encontra o slot no appointment
      const slotReference = dataAppointment.slot?.[0]?.reference;

      if (patientParticipant?.actor?.reference && slotReference) {
        const filter = `status=active&_sort=-_lastUpdated&request=${slotReference}&response=${patientParticipant.actor.reference}&_count=1`;
        const response = await GetPaymentNotice({ filter });
        setPayment(response?.data?.entry);

        // Verifica se existe paymentStatus no PaymentNotice
        const hasPaymentStatus =
          response?.data?.entry?.[0]?.resource?.paymentStatus;
        setIsPaid(!!hasPaymentStatus);
      }
    }
  };

  useEffect(() => {
    fetchPayment();
  }, [dataAppointment?.id]);

  return (
    <>
      <Stack width="100%" gap={{ xs: 1, md: 2 }}>
        {access?.roles?.includes("practitioner") && (
          <Button
            fullWidth
            sx={{
              fontSize: 16,
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
              textTransform: "none",
              color: "neutral0.main",
              backgroundColor: `${
                primaryButton.backgroundColor[status || "pending"]
              }`,
              ":hover": {
                backgroundColor: `${primaryButton.hover[status || "pending"]}`,
              },
              ":disabled": {
                border: `1px solid`,
                borderColor: "neutral300.main",
                color: "neutral600.main",
                backgroundColor: "neutral300.main",
              },
            }}
            disabled={loading}
            onClick={async () => {
              const verifyRelated = await fetchRelated();

              if (verifyRelated?.length) {
                setVerifyDialog(true);
              } else {
                // Verificar se o atendimento foi pago
                const isPaid = checkAppointmentPayment();

                if (!isPaid) {
                  // Se não foi pago, mostrar alerta
                  setWarningAction("startService");
                  setShowPaymentWarning(true);
                } else {
                  // Se foi pago, continuar normalmente
                  setLoading(true);
                  await verifyEncounter(dataAppointment);
                }
              }
            }}
          >
            {loading ? (
              <CircularProgress size={25} />
            ) : (
              secondaryButtonStyle.icon[status]
            )}
            {inProgress === dataAppointment?.id
              ? "Continuar atendimento"
              : secondaryButtonStyle.text[status]}
          </Button>
        )}

        <Stack direction="row" spacing={2}>
          {access?.roles?.includes("practitioner") && (
            <Button
              fullWidth
              sx={styleSXSecondary}
              onClick={() => {
                // Verificar se o atendimento foi pago
                const isPaid = checkAppointmentPayment();

                if (!isPaid) {
                  // Se não foi pago, mostrar alerta
                  setWarningAction("navigate");
                  setShowPaymentWarning(true);
                } else {
                  // Se foi pago, navegar normalmente
                  navigateToSummary();
                }
              }}
            >
              {primaryButton.icon} {primaryButton.text[status]}
            </Button>
          )}

          <Button
            fullWidth
            sx={{
              gap: 1,
              border: "1px solid",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
              fontSize: 16,
              textTransform: "none",
              backgroundColor: "transparent",
              borderColor: "#F8727D",
              color: "#F8727D",
              ":hover": {
                backgroundColor: "neutral0.main",
              },
            }}
            onClick={() => setIsOpen(true)}
          >
            <Close /> Cancelar consulta
          </Button>
        </Stack>
      </Stack>

      {verifyDialog && (
        <VerifyResponsibleDialog
          handleClose={() => setVerifyDialog(false)}
          isOpen={true}
          loading={loading}
          setLoading={setLoading}
          dataAppointment={dataAppointment}
          related={related}
          fetchRelated={fetchRelated}
          patientId={patientId}
        />
      )}

      {/* Componente de aviso de pagamento */}
      <WarningAppointment
        isOpen={showPaymentWarning}
        handleClose={() => setShowPaymentWarning(false)}
        onConfirm={() => {
          setShowPaymentWarning(false);

          if (warningAction === "navigate") {
            navigateToSummary();
          } else if (warningAction === "startService") {
            setLoading(true);
            verifyEncounter(dataAppointment);
          }
        }}
        appointmentDescription="Esta consulta"
        appointment={dataAppointment}
        hasSubscriptionFluxPay={hasSubscriptionFluxPay}
        onPaymentSent={fetchPayment}
      />
    </>
  );
}
